import {Component, Inject, OnInit, Optional, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {UntypedFormControl} from '@angular/forms';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import { AuthorizationService } from 'src/app/services/authorization/authorization.service';

import {QuestionCreateComponent} from './question-create/question-create.component';
import {QuestionUpdateComponent} from './question-update/question-update.component';
import {QuestionDeleteComponent} from './question-delete/question-delete.component';
import {CategoryPopupComponent} from './categories/category-popup.component';
import {Category, Pageable, Question} from 'src/app/models';
import {FaqService, SnackbarService} from 'src/app/services';
import {ApplicationConstant} from 'src/app/utilities/ApplicationConstant';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {AutocompleteComponent, ButtonConfig, FilterConfig} from '../../components';
import {TitleCasePipe} from '@angular/common';

@Component({
    selector: 'app-faq',
    templateUrl: './faq.component.html',
    styleUrls: ['./faq.component.scss'],
    providers: [TitleCasePipe]
})

export class FaqComponent implements OnInit {
    group = false;
    contact = false;
    admin = false;
    categories: Category[] = [];
    questions = [];
    selectedCategory = new Category(null, '', []);
    selected = new UntypedFormControl(0);

    // search stuff
    @ViewChild('autoComplete', {static: true}) autoComplete: AutocompleteComponent;

    searchCategory = null;
    searchTerm = '';
    searchFilterId = null;
    searchQuestions: Question[] = [];
    searchChange = new Subject<string>();
    questionText = '';
    filterConfig: FilterConfig;
    buttonConfig: ButtonConfig;
    searchQuestionsFormatted: string[] = [];
    pageNum = 1;

    requestedCategory: string;
    requestedQuestion: string;

    constructor(public faqService: FaqService,
                private dialog: MatDialog,
                private authzService: AuthorizationService,
                private snackbarService: SnackbarService,
                private questionService: FaqService,
                private _route: ActivatedRoute,
                public titleCasePipe: TitleCasePipe,
                @Optional() @Inject(MAT_DIALOG_DATA) public data: any) {}

    ngOnInit() {
        this._route.params.subscribe(params => {
            this.requestedCategory = params['category']
            this.requestedQuestion = params['question']
        });

        this.faqService.getCategories().subscribe(categories => {
            this.categories = categories;

            if (this.data || this.requestedCategory) {
                this.selectCategory(this.data.category ? this.getCategoryFromName(this.data.category) : this.getCategoryFromName(this.requestedCategory));
                if (this.data.question || this.requestedQuestion) {
                    this.selectQuestion( this.data.question ?  this.data.question : this.requestedQuestion);
                }
            }

            this.loadData();
        });
    }

    onTabChange(event: MatTabChangeEvent) {
        this.selectedCategory = this.categories.find(category => category.name.toLowerCase() === event.tab.textLabel.toLowerCase());
        if (this.selectedCategory && !this.selectedCategory.questions) {
            this.questions = this.selectedCategory.questions;
        } else {
            this.questions = [];
        }
    }

    trackById(index, item) {
        return item.id;
    }

    loadData() {
        this.admin = false;
        let categoryNames = ['All'].concat(this.categories.map((c) => this.titleCasePipe.transform(c.name)))
        this.filterConfig = {
            enable: true,
            filterOptions: categoryNames,
            defaultOption: 'All',
            filterHint: 'Select a Category'
        };
        this.authzService.hasAccess([ApplicationConstant.getAdminRole()]).subscribe(isAdmin => this.admin = isAdmin);
        const delay = 750;
        this.searchChange.pipe(debounceTime(delay), distinctUntilChanged()).subscribe((term: string) => this.searchForQuestions(term, this.getCategoryIdFromFilter()));
    }

    getQuestions() {
        this.faqService.getCategories().subscribe(categories => {
            this.categories = categories;
            this.selectedCategory = this.categories.find(category => category.id === this.selectedCategory.id);
            this.selectedCategory = this.selectedCategory === undefined ? this.categories[0] : this.selectedCategory;
            if (this.selectedCategory && this.selectedCategory.questions == undefined) {
                this.selectedCategory.questions = [];
            }
        });
    }

    searchForQuestions(term: string, categoryId: number) {
        this.searchTerm = term.trim().length ? term.trim() : '';
        this.searchFilterId = categoryId ? categoryId : null;
        if (this.searchTerm.length >= 2) {
            this.faqService.searchQuestions(this.searchTerm, categoryId).subscribe((questions: Question[]) => {
                this.searchQuestions = questions;
                this.searchQuestionsFormatted = questions.map(q => q.question);
            });
        }
    }

    getCategoryIdFromFilter(): number {
        if (this.autoComplete.filterControl.value === 'All') return null;
        return this.categories.find((c) => c.name === this.autoComplete.filterControl.value).id;
    }

    searchQuestionSelected(event?: any) {
        let selection = this.searchQuestions.find(q => q.question === event.option.value)
        this.selectCategory(selection.category);
        this.selectQuestion(selection.question);
        this.searchChange.next('');
    }

    getCategoryFromName(categoryName: string): Category {
        return this.categories.find((c) => c.name.toLowerCase() === categoryName.toLowerCase());
    }

    selectCategory(category: Category) {
        let id = this.categories.findIndex((c) => c.id === category.id)
        this.selected = new UntypedFormControl(id);
    }

    selectQuestion(questionText: string) {
        this.questionText = questionText;
    }

    newQuestionPopup() {
        const popupDialog = this.dialog.open(QuestionCreateComponent, {
            restoreFocus: false,
            width: '85%',
            data: {currentCategory: this.selectedCategory}
        });
        popupDialog.afterClosed().subscribe(question => {
            if (question) {
                this.questionService.createQuestion(question).subscribe({
                    next: () => {
                        this.getQuestions();
                        this.snackbarService.openSnackBar('Question created', false);
                    }, error: err => this.snackbarService.openSnackBar('Creation Failure. ERROR: ' + err.statusText, true)
                });
            }
        });
    }

    delete(question ?: any) {
        const popupDialog = this.dialog.open(QuestionDeleteComponent, {
            width: '85%',
            data: {question: question}
        });
        popupDialog.afterClosed().subscribe(result => {
            if (result) {
                this.questionService.deleteQuestion(question.id).subscribe({
                    next: () => {
                        this.getQuestions();
                        this.snackbarService.openSnackBar('Question Deleted', false);
                    }, error: () => this.snackbarService.openSnackBar('Question NOT Deleted', false)
                });
            }
        });
    }

    update(question ?: any) {
        const popupDialog = this.dialog.open(QuestionUpdateComponent, {
            width: '85%',
            data: {question: question}
        });
        popupDialog.afterClosed().subscribe({
            next: result => {
                if (result) {
                    this.questions[this.selected.value] = null;
                    this.getQuestions();
                    this.snackbarService.openSnackBar('Question updated', false);
                }
            }, error: () => this.snackbarService.openSnackBar('Question NOT updated', true)
        });
    }

    categoryPopup() {
        const popupDialog = this.dialog.open(CategoryPopupComponent, {
            width: '55%',
            height: '75%',
            panelClass: 'faq-category-dialog'
        });
        popupDialog.afterClosed().subscribe(() => {
            this.getQuestions();
        });
    }

    onSearchChanged($event: any) {
        if ($event != undefined) {
            this.searchForQuestions($event,this.getCategoryIdFromFilter());
        }
    }

    load(page: any) {
        if (page.data === undefined) {
            this.faqService.searchQuestionsPage(this.searchTerm, page * 15, this.getCategoryIdFromFilter()).subscribe((questions: Pageable<Question>) => {
                this.searchQuestions = questions.content;
                this.searchQuestionsFormatted = questions.content.map(q => q.question);
            });
        }
    }

}
